import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, finalize, map, share, switchMap, tap } from 'rxjs/operators';
import { RegistrationStepsResponse, User } from 'src/app/interfaces/user';
import { UserService } from '../user/user.service';
import {
  RegistrationFlowStep,
  RegistrationFlowStepId,
  RegistrationFlowType,
  STEP_ROUTES,
} from 'src/app/constants/registration-flow.constants';
import { TOK_DETAILS, TOK_PAYMENT } from 'src/app/constants/route.constants';

/**
 * The steps the TOK pages (tok-details, then tok-payment) stand in for, for TOK requests other than
 * B21K, which have no step of their own.
 */
const TOK_FLOW_STEPS: string[] = [
  RegistrationFlowStep.UserDetails,
  RegistrationFlowStep.PetDetails,
  RegistrationFlowStep.MembershipOptions,
];

/**
 * Owns the per-user registration flow from /api/registration/me/steps: the ordered steps, the current
 * step, which steps are present or done, the next step/route, completing steps and where a returning
 * user resumes. Caches the steps once per session. Payment/pricing config lives in PaymentConfigService.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationFlowService {
  private cachedFlow: RegistrationStepsResponse | null = null;
  private inFlightFlow$: Observable<RegistrationStepsResponse | null> | null = null;
  private isBrowser: boolean;

  constructor(
    private userService: UserService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  public clear(): void {
    this.cachedFlow = null;
    this.inFlightFlow$ = null;
  }

  /** Fetches the ordered steps for this user once and caches them. */
  public loadSteps(): Observable<string[] | null> {
    return this.loadFlow().pipe(map((flow) => flow?.steps ?? null));
  }

  /** The cached steps, or null if not loaded yet. */
  public getSteps(): string[] | null {
    return this.cachedFlow?.steps ?? null;
  }

  /** The cached current step, or null if not loaded yet. */
  public getCurrentStep(): string | null {
    return this.cachedFlow?.currentStep ?? null;
  }

  /**
   * Whether a step exists in the user's flow. Defaults to true until the steps are loaded, so
   * consumers keep their existing behaviour during the brief async gap, or if /steps is unavailable.
   */
  public isStepPresent(stepId: string): boolean {
    return this.cachedFlow ? this.cachedFlow.steps.includes(stepId) : true;
  }

  /**
   * Whether the user has completed a step: they have moved past it, or their flow doesn't have it.
   * False until the steps are loaded.
   */
  public isStepCompleted(stepId: string): boolean {
    if (!this.cachedFlow) {
      return false;
    }
    const { steps, currentStep } = this.cachedFlow;
    return !steps.includes(stepId) || steps.indexOf(stepId) < steps.indexOf(currentStep);
  }

  /** Whether the user is in the B21K flow. */
  public isB21KFlow(flow = this.cachedFlow): boolean {
    return flow?.type === RegistrationFlowType.B21K;
  }

  /** The TOK page of the B21K keeper's current step, or null when they aren't on one. */
  public getB21KRoute(): string | null {
    const currentStep = this.cachedFlow?.currentStep;
    const isOnTokStep =
      currentStep === RegistrationFlowStep.UserAndPetDetails ||
      currentStep === RegistrationFlowStep.MembershipOptions;
    return this.isB21KFlow() && isOnTokStep ? this.getStepPage(currentStep, this.cachedFlow) : null;
  }

  /**
   * Completes a step on the backend so a returning user resumes after it, then reads the steps again, since
   * the backend can move the current step past more than one step (the POST returns no body). Moves the
   * cached current step to the next one first, so it still moves on when the read fails. Skips the call for
   * a step that is already completed, for example when a keeper adding a pet goes through the steps again,
   * as the backend only moves the current step for a step at or after it. Never errors: a failed call is
   * logged and callers carry on navigating.
   */
  public completeStep(stepId: RegistrationFlowStepId): Observable<void> {
    if (!this.isBrowser) {
      return of(undefined);
    }
    return this.loadSteps().pipe(
      switchMap(() => {
        if (this.isStepCompleted(stepId)) {
          return of(undefined);
        }
        return this.userService.completeRegistrationStep(stepId).pipe(
          tap(() => {
            const nextStepId = this.getNextStepId(stepId);
            if (this.cachedFlow && nextStepId) {
              this.cachedFlow = { ...this.cachedFlow, currentStep: nextStepId };
            }
          }),
          switchMap(() => {
            // A read still running started before the POST, so it can't replace the cache; start a new one
            this.inFlightFlow$ = null;
            return this.fetchFlow();
          }),
          map(() => undefined)
        );
      }),
      catchError((err) => {
        console.error(`Error completing registration step ${stepId}`, err);
        return of(undefined);
      })
    );
  }

  /**
   * The route a returning user should be sent to: their current step's route, or null when the
   * registration is complete, the current step has no route or /steps is unavailable. Refetches the
   * steps by default, since a payment completes paymentOptions on the backend; useLoadedSteps reuses
   * the steps already loaded or loading instead (e.g. right after login, when the cache can only hold
   * this session's steps).
   */
  public getResumeRoute(user: User, useLoadedSteps = false): Observable<string | null> {
    return (useLoadedSteps ? this.loadFlow() : this.fetchFlow()).pipe(
      map((flow) => {
        if (!flow || flow.currentStep === RegistrationFlowStep.Dashboard) {
          return null;
        }

        // Other TOK requests have no step of their own, so they go to the TOK details page.
        if (
          !this.isB21KFlow(flow) &&
          user.tokRequests.length > 0 &&
          TOK_FLOW_STEPS.includes(flow.currentStep)
        ) {
          return `/${TOK_DETAILS}`;
        }

        return this.getStepRoute(flow.currentStep, user, flow);
      })
    );
  }

  /**
   * The id of the step that follows currentStepId in the user's flow, or null when the steps aren't
   * loaded or the current step is the last one.
   */
  public getNextStepId(currentStepId: string): string | null {
    const steps = this.cachedFlow?.steps;
    if (!steps) {
      return null;
    }
    const index = steps.indexOf(currentStepId);
    if (index === -1 || index === steps.length - 1) {
      return null;
    }
    return steps[index + 1];
  }

  /**
   * The route for the step that follows currentStepId, or null when there is no next step or it has
   * no mapped route. Callers fall back to their existing navigation in that case.
   */
  public getNextRoute(currentStepId: string): string | null {
    const nextStepId = this.getNextStepId(currentStepId);
    return nextStepId ? STEP_ROUTES[nextStepId as RegistrationFlowStepId] ?? null : null;
  }

  private loadFlow(): Observable<RegistrationStepsResponse | null> {
    return this.cachedFlow ? of(this.cachedFlow) : this.fetchFlow();
  }

  /** Fetches the steps, sharing a request that is still running so parallel callers make one call. */
  private fetchFlow(): Observable<RegistrationStepsResponse | null> {
    if (!this.isBrowser) {
      return of(null);
    }
    if (!this.inFlightFlow$) {
      // Only the current request touches the cache, so one still running at logout (clear) can't write the
      // previous user's steps or drop a newer request
      const request$: Observable<RegistrationStepsResponse | null> = this.userService.getRegistrationSteps().pipe(
        // A current step that isn't one of the steps can't be placed in the flow, so it counts as unavailable
        map((response) => (response.steps?.includes(response.currentStep) ? response : null)),
        tap((flow) => this.inFlightFlow$ === request$ && (this.cachedFlow = flow)),
        catchError(() => of(null)),
        finalize(() => this.inFlightFlow$ === request$ && (this.inFlightFlow$ = null)),
        // Replays the response, so a caller joining after it arrives but before the request completes still gets it
        share({ connector: () => new ReplaySubject(1) })
      );
      this.inFlightFlow$ = request$;
    }
    return this.inFlightFlow$;
  }

  /**
   * petDetails depends on the user's pets: confirm the first pet still to be completed (or the first pet
   * when all are), or claim a new one. Null for a step the FE has no route for.
   */
  private getStepRoute(stepId: string, user: User, flow: RegistrationStepsResponse): string | null {
    if (stepId === RegistrationFlowStep.PetDetails && user.pets.length > 0) {
      // Sorts a copy, since getPetsForUserOrderedById sorts the pets array it is given
      const pets = this.userService.getPetsForUserOrderedById({ ...user, pets: [...user.pets] });
      const petToConfirm =
        pets.find((pet) => pet.name === null || pet.name === '[to_be_completed_by_keeper]') ?? pets[0];
      return `/my-pet/${petToConfirm.id}/confirm`;
    }
    return this.getStepPage(stepId, flow);
  }

  /** The page for a step, or null when the FE has no route for it. */
  private getStepPage(stepId: string, flow: RegistrationStepsResponse): string | null {
    // The B21K flow pays on the TOK payment page.
    if (stepId === RegistrationFlowStep.MembershipOptions && this.isB21KFlow(flow)) {
      return `/${TOK_PAYMENT}`;
    }
    return STEP_ROUTES[stepId as RegistrationFlowStepId] ?? null;
  }
}
